import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaBars, FaTimes, FaRobot } from 'react-icons/fa';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: ${props => props.scrolled ? 'var(--white)' : 'transparent'};
  box-shadow: ${props => props.scrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none'};
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  padding: ${props => props.scrolled ? '0.75rem 0' : '1.25rem 0'};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const LogoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: white;
  font-size: 1.5rem;
  margin-right: 0.75rem;
`;

const LogoText = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(to right, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color:rgba(251, 248, 252, 0.37);
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${props => props.isOpen ? '0' : '-100%'};
    width: 75%;
    max-width: 300px;
    height: 100vh;
    background-color: var(--white);
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 5rem;
    transition: right 0.3s ease;
    z-index: 1001;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const NavItem = styled.li`
  margin: 0 1rem;
  
  @media (max-width: 768px) {
    margin: 0;
    width: 100%;
    text-align: center;
  }
`;

const NavLink = styled.a`
  text-decoration: none;
  color: ${props => props.scrolled ? 'var(--text-dark)' : 'var(--white)'};
  font-weight: 500;
  padding: 0.5rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    color: var(--text-dark);
    display: block;
    padding: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  background: linear-gradient(to right, var(--primary-color), var(--secondary-color));
  color: var(--white);
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  text-decoration: none;
  margin-left: 1.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(var(--primary-rgb), 0.3);
  }
  
  @media (max-width: 768px) {
    margin: 1.5rem 0 0;
    width: 80%;
    text-align: center;
  }
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => props.scrolled ? 'var(--text-dark)' : 'var(--white)'};
  
  @media (max-width: 768px) {
    display: block;
    z-index: 1002;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-dark);
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const closeMenu = () => {
    setIsMenuOpen(false);
  };
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);
  
  return (
    <HeaderContainer scrolled={scrolled}>
      <Container>
        <LogoContainer>
          <LogoIcon>
            <FaRobot />
          </LogoIcon>
          <LogoText>TinySmart.AI</LogoText>
        </LogoContainer>
        
        <MenuToggle scrolled={scrolled} onClick={toggleMenu}>
          <FaBars />
        </MenuToggle>
        
        <Nav isOpen={isMenuOpen}>
          <CloseButton onClick={closeMenu}>
            <FaTimes />
          </CloseButton>
          
          <NavLinks>
            <NavItem>
              <NavLink href="#features" scrolled={scrolled} onClick={closeMenu}>Features</NavLink>
            </NavItem>
            <NavItem>
              <NavLink href="#how-it-works" scrolled={scrolled} onClick={closeMenu}>How It Works</NavLink>
            </NavItem>
            <NavItem>
              <NavLink href="#showcase" scrolled={scrolled} onClick={closeMenu}>Product</NavLink>
            </NavItem>
            <NavItem>
              <NavLink href="#contact" scrolled={scrolled} onClick={closeMenu}>Contact</NavLink>
            </NavItem>
          </NavLinks>
          
          <CTAButton href="#contact">Get Early Access</CTAButton>
        </Nav>
      </Container>
    </HeaderContainer>
  );
};

export default Header; 